import type { NumericFilterFieldType } from 'features/charts-v2/types'

// Discriminates the five special (non-field) editing panes in
// FilterSelectionPopover. Only one can be open at a time; `null` means the
// popover is showing a regular field or the "All" view.
export type PopoverPane =
  | 'sentiment'
  | 'keywords'
  | 'smart_search'
  | NumericFilterFieldType

// Leading mark on a row: `#` a numeric filter, `Aa` a text one.
export type SidebarGlyph = 'Aa' | '#' | '★' | '◉'

// What a selectable row may carry for display; a `group` header has its own
// `description` and no glyph.
type SidebarRowDecor = { glyph?: SidebarGlyph; description?: string }

export type LeftSidebarItem =
  | ({ name: string; type: 'saved' } & SidebarRowDecor)
  | { name: string; type: 'group'; description?: string }
  | ({ name: string; type: 'keywords' } & SidebarRowDecor)
  | ({ name: string; type: 'smart_search' } & SidebarRowDecor)
  | ({ name: string; type: 'sentiment' } & SidebarRowDecor)
  | ({ name: string; type: 'category' } & SidebarRowDecor)
  | ({ name: string; type: 'field'; field: string } & SidebarRowDecor)
  | ({
      name: string
      type: 'numeric'
      numericField: NumericFilterFieldType
    } & SidebarRowDecor)

/**
 * Whether a left-sidebar item is the one currently open in the popover.
 *
 * Mirrors the state each item type writes in `handleLeftSideClick` — the two
 * must stay in sync when a sidebar item type is added.
 */
export const isSidebarItemSelected = (
  item: LeftSidebarItem,
  selection: {
    view: 'fields' | 'saved'
    selectedField: string | null
    pane: PopoverPane | null
  },
): boolean => {
  const { view, selectedField, pane } = selection
  switch (item.type) {
    case 'saved':
      return view === 'saved'
    case 'sentiment':
      return pane === 'sentiment'
    case 'keywords':
      return pane === 'keywords'
    case 'smart_search':
      return pane === 'smart_search'
    case 'numeric':
      return pane === item.numericField
    case 'category':
      return view === 'fields' && selectedField === null && pane === null
    case 'field':
      return view === 'fields' && selectedField === item.field
    case 'group':
      return false
  }
}
